import React from 'react';
import { DecodedRouteParams, Route } from 'config/routes';
import UploadBulletinLink from 'components/upload-bulletin-link';
import { DealType, MarketplaceType } from 'utils/entities';
import { PropertyType, RangeTuple, Seller } from 'components/filters/types';

export enum LinkType {
  RouterLink = 'RouterLink',
  ALink = 'ALink',
}

interface FooterAnchorLink {
  text: string;
  type: LinkType;
  routeName?: Route;
  routeParams?: DecodedRouteParams;
  externalPath?: string;
  externalParams?: Record<string, string>;
}

interface FooterComponentLink {
  Component: JSX.Element;
}

export type FooterLink = FooterAnchorLink | FooterComponentLink;

interface FooterSection {
  header: string;
  links: FooterLink[];
}

const hebrewBaseUrl = '/';

const ZERO_TO_500_K: RangeTuple<number> = [ 0, 500000 ];
const ZERO_TO_10_M: RangeTuple<number> = [ 0, 10000000 ];
const ZERO_TO_100_K_PPM: RangeTuple<number> = [ 0, 100000 ];
const ZERO_TO_10_K_PPM: RangeTuple<number> = [ 0, 10000 ];

const baseFooterSections = [
  {
    header: 'כלים',
    links: [
      {
        text: 'בלוג מדל״ן',
        type: LinkType.ALink,
        externalPath: 'https://www.madlan.co.il/blog',
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'אינדקס יזמים',
        type: LinkType.RouterLink,
        routeName: Route.DevelopersSearchPage,
        routeParams: { source: 'footer_link' },
      },
      {
        text: 'מאגר המתווכים',
        type: LinkType.RouterLink,
        routeName: Route.MadadAgentPage,
        routeParams: { source: 'footer_link' },
      },
      {
        text: 'מדד המתווכים 2025/26',
        type: LinkType.RouterLink,
        routeName: Route.MadadPage,
        routeParams: { source: 'footer_link' },
      },
      {
        text: 'מדד המתווכים 2024/25',
        type: LinkType.RouterLink,
        routeName: Route.MadadArchivePage,
        routeParams: { year: 2025, source: 'footer_link' },
      },
      {
        text: 'מחיר למשתכן',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}mechirLamishtaken/introduction`,
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'מחשבון זכאות',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}mechirLamishtaken/eligible`,
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'כתבות נדל"ן',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}article/main`,
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'מחשבון מס רכישה',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}appartmentTax/calculator`,
        externalParams: { source: 'footer_link' },
      },
    ],
  },
  {
    header: 'מידע',
    links: [
      {
        text: 'פרסום ליזמים',
        type: LinkType.RouterLink,
        routeName: Route.DevelopersPromotionPage,
        routeParams: { source: 'footer_link' },
      },
      {
        text: 'פרסום למתווכים',
        type: LinkType.RouterLink,
        routeName: Route.AgentsPromotionPage,
        routeParams: { source: 'footer_link' },
      },
      {
        text: 'מערכת מקצועית ליזמים',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}professional`,
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'מערכת מקצועית למתווכים',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}agent/main`,
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'דרושים',
        type: LinkType.ALink,
        externalPath: 'https://www.madlan.co.il/blog/careers',
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'שאלות ותשובות',
        type: LinkType.ALink,
        externalPath: 'https://help.madlan.co.il/hc/he/categories/29518300674461-%D7%A9%D7%90%D7%9C%D7%95%D7%AA-%D7%95%D7%AA%D7%A9%D7%95%D7%91%D7%95%D7%AA-%D7%9C%D7%92%D7%95%D7%9C%D7%A9%D7%99-%D7%9E%D7%93%D7%9C%D7%9F',
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'אודות',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}etc/aboutUs`,
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'תנאי שימוש',
        type: LinkType.RouterLink,
        routeName: Route.TermsPage,
        routeParams: { source: 'footer_link' },
      },
      {
        text: 'הצהרת נגישות',
        type: LinkType.RouterLink,
        routeName: Route.Accessibility,
        routeParams: { source: 'footer_link' },
      },
      {
        text: 'צור קשר',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}lp/connectUs`,
        externalParams: { source: 'footer_link' },
      },
    ],
  },
];

const RENT_RESIDENTIAL_SECTION_HEADER = 'דירות להשכרה';

const footerSectionsResidential: FooterSection[] = [
  {
    header: 'לוח דירות',
    links: [
      {
        text: 'לוח דירות למכירה',
        type: LinkType.RouterLink,
        routeName: Route.Search,
        routeParams: { source: 'footer_link', dealType: DealType.Buy, term: [ 'ישראל' ] },
      },
      {
        text: 'לוח דירות להשכרה',
        type: LinkType.RouterLink,
        routeName: Route.Search,
        routeParams: { source: 'footer_link', dealType: DealType.Rent, term: [ 'ישראל' ] },
      },
      {
        text: 'דירות חדשות ופרויקטים חדשים',
        type: LinkType.RouterLink,
        routeName: Route.Search,
        routeParams: { source: 'footer_link', dealType: DealType.Buy, filters: { seller: [ Seller.Developer ], priceRange: [ 0, 25000000 ] }, term: [ 'ישראל' ] },
      },
      {
        Component: <UploadBulletinLink source="home_page_footer_bar" text="פרסום דירה בחינם!" />,
      },
    ],
  },
  {
    header: 'דירות למכירה',
    links: [
      {
        text: 'דירות למכירה בכל הארץ',
        routeParams: { term: [ 'ישראל' ] },
      },
      {
        text: 'דירות למכירה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ] },
      },
      {
        text: 'דירות למכירה בירושלים',
        routeParams: { term: [ 'ירושלים-ישראל' ] },
      },
      {
        text: 'דירות למכירה בהרצליה',
        routeParams: { term: [ 'הרצליה-ישראל' ] },
      },
      {
        text: 'דירות למכירה ברמת גן',
        routeParams: { term: [ 'רמת-גן-ישראל' ] },
      },
      {
        text: 'דירות למכירה בגבעתיים',
        routeParams: { term: [ 'גבעתיים-ישראל' ] },
      },
      {
        text: 'דירות למכירה בחיפה',
        routeParams: { term: [ 'חיפה-ישראל' ] },
      },
      {
        text: 'דירות למכירה באשדוד',
        routeParams: { term: [ 'אשדוד-ישראל' ] },
      },
      {
        text: 'דירות למכירה בפתח תקווה',
        routeParams: { term: [ 'פתח-תקווה-ישראל' ] },
      },
      {
        text: 'דירות למכירה בחולון',
        routeParams: { term: [ 'חולון-ישראל' ] },
      },
      {
        text: 'דירות למכירה ברחובות',
        routeParams: { term: [ 'רחובות-ישראל' ] },
      },
    ].map(el => ({ ...el,
      type: LinkType.RouterLink,
      routeName: Route.Search,
      routeParams: {
        ...el.routeParams,
        dealType: DealType.Buy,
        tracking_search_source: 'homepage_footer',
      },
    })),
  },
  {
    header: RENT_RESIDENTIAL_SECTION_HEADER,
    links: [
      {
        text: 'דירות להשכרה בכל הארץ',
        routeParams: { term: [ 'ישראל' ] },
      },
      {
        text: 'דירות להשכרה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ] },
      },
      {
        text: 'דירות להשכרה בירושלים',
        routeParams: { term: [ 'ירושלים-ישראל' ] },
      },
      {
        text: 'דירות להשכרה בהרצליה',
        routeParams: { term: [ 'הרצליה-ישראל' ] },
      },
      {
        text: 'דירות להשכרה ברמת גן',
        routeParams: { term: [ 'רמת-גן-ישראל' ] },
      },
      {
        text: 'דירות להשכרה בגבעתיים',
        routeParams: { term: [ 'גבעתיים-ישראל' ] },
      },
      {
        text: 'דירות להשכרה בחיפה',
        routeParams: { term: [ 'חיפה-ישראל' ] },
      },
      {
        text: 'דירות להשכרה באשדוד',
        routeParams: { term: [ 'אשדוד-ישראל' ] },
      },
      {
        text: 'דירות להשכרה בפתח תקווה',
        routeParams: { term: [ 'פתח-תקווה-ישראל' ] },
      },
      {
        text: 'דירות להשכרה בחולון',
        routeParams: { term: [ 'חולון-ישראל' ] },
      },
      {
        text: 'דירות להשכרה ברחובות',
        routeParams: { term: [ 'רחובות-ישראל' ] },
      },
    ].map(el => ({ ...el,
      type: LinkType.RouterLink,
      routeName: Route.Search,
      routeParams: {
        ...el.routeParams,
        dealType: DealType.Rent,
        tracking_search_source: 'homepage_footer',
      },
    })),
  },
  {
    header: 'שכונות',
    links: [
      {
        text: 'מפת הנדל"ן של ישראל',
        type: LinkType.ALink,
        externalPath: `${hebrewBaseUrl}local/pricesHeatmap`,
        externalParams: { source: 'footer_link' },
      },
      {
        text: 'שכונות בתל אביב',
        type: LinkType.RouterLink,
        routeParams: { id: 'תל-אביב-יפו-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות בירושלים',
        type: LinkType.RouterLink,
        routeParams: { id: 'ירושלים-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות בהרצליה',
        type: LinkType.RouterLink,
        routeParams: { id: 'הרצליה-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות ברמת גן',
        type: LinkType.RouterLink,
        routeParams: { id: 'רמת-גן-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות בגבעתיים',
        type: LinkType.RouterLink,
        routeParams: { id: 'גבעתיים-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות בחיפה',
        type: LinkType.RouterLink,
        routeParams: { id: 'חיפה-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות באשדוד',
        type: LinkType.RouterLink,
        routeParams: { id: 'אשדוד-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות בפתח תקווה',
        type: LinkType.RouterLink,
        routeParams: { id: 'פתח-תקווה-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות בחולון',
        type: LinkType.RouterLink,
        routeParams: { id: 'חולון-ישראל', source: 'footer_link' },
      },
      {
        text: 'שכונות ברחובות',
        type: LinkType.RouterLink,
        routeParams: { id: 'רחובות-ישראל', source: 'footer_link' },
      },
    ].map(el => el.type && el.type === LinkType.ALink ? el : ({ ...el,
      routeName: Route.LocalPage,
      routeParams: { ...el.routeParams, source: 'footer_link' },
    })),
  },
  ...baseFooterSections,
];

const footerSectionsCommercial: FooterSection[] = [
  {
    header: 'נדל״ן מסחרי',
    links: [
      {
        text: 'נדל״ן מסחרי למכירה',
        type: LinkType.RouterLink,
        routeName: Route.SearchCommercial,
        routeParams: { source: 'footer_link', dealType: DealType.Buy, term: [ 'ישראל' ] },
      },
      {
        text: 'נדל״ן מסחרי להשכרה',
        type: LinkType.RouterLink,
        routeName: Route.SearchCommercial,
        routeParams: { source: 'footer_link', dealType: DealType.Rent, term: [ 'ישראל' ] },
      },
      {
        text: 'פרויקטים חדשים',
        type: LinkType.RouterLink,
        routeName: Route.SearchCommercial,
        routeParams: { source: 'footer_link', dealType: DealType.Buy, term: [ 'ישראל' ], filters: {
          priceRange: [ 0, 10000000 ],
          seller: [ Seller.Developer ],
          ppmRange: [ 0, 100000 ],
        },
        },
      },
      {
        text: 'פרסום נכס מסחרי',
        type: LinkType.RouterLink,
        routeName: Route.UploadBulletin,
        routeParams: { source: 'commercial_landing_page_footer' },
      },
    ],
  },
  {
    header: 'נכסים מסחריים למכירה',
    links: [
      {
        text: 'נכסים מסחריים למכירה בכל הארץ',
        routeParams: { term: [ 'ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה ברמת גן',
        routeParams: { term: [ 'רמת-גן-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה בהרצליה',
        routeParams: { term: [ 'הרצליה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה בנתניה',
        routeParams: { term: [ 'נתניה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה בפתח תקווה',
        routeParams: { term: [ 'פתח-תקווה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה בירושלים',
        routeParams: { term: [ 'ירושלים-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה בחיפה',
        routeParams: { term: [ 'חיפה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה ברעננה',
        routeParams: { term: [ 'רעננה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה ברחובות',
        routeParams: { term: [ 'רחובות-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים למכירה בחולון',
        routeParams: { term: [ 'חולון-ישראל' ] },
      },
    ].map(el => ({ ...el,
      type: LinkType.RouterLink,
      routeName: Route.SearchCommercial,
      routeParams: { ...el.routeParams, dealType: DealType.Buy, source: 'footer_link' },
    })),
  },
  {
    header: 'נכסים מסחריים להשכרה',
    links: [
      {
        text: 'נכסים מסחריים להשכרה בכל הארץ',
        routeParams: { term: [ 'ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה ברמת גן',
        routeParams: { term: [ 'רמת-גן-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה בהרצליה',
        routeParams: { term: [ 'הרצליה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה בנתניה',
        routeParams: { term: [ 'נתניה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה בפתח תקווה',
        routeParams: { term: [ 'פתח-תקווה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה בירושלים',
        routeParams: { term: [ 'ירושלים-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה בחיפה',
        routeParams: { term: [ 'חיפה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה ברעננה',
        routeParams: { term: [ 'רעננה-ישראל' ] },
      },
      {
        text: 'נכסים מסחריים להשכרה ברחובות',
        routeParams: { term: [ 'רחובות-ישראל' ] },
      },
      {
        text: 'משרדים להשכרה בחולון',
        routeParams: { term: [ 'חולון-ישראל' ] },
      },
    ].map(el => ({ ...el,
      type: LinkType.RouterLink,
      routeName: Route.SearchCommercial,
      routeParams: { ...el.routeParams, dealType: DealType.Rent, source: 'footer_link' },
    })),
  },
  {
    header: 'חיפושים פופולריים',
    links: [
      {
        text: 'משרדים להשכרה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'חנויות להשכרה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ], filters: { propertyTypes: [ PropertyType.Shop ] } },
      },
      {
        text: 'משרדים להשכרה ברמת גן',
        routeParams: { term: [ 'רמת-גן-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בהרצליה',
        routeParams: { term: [ 'הרצליה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בנתניה',
        routeParams: { term: [ 'נתניה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בפתח תקווה',
        routeParams: { term: [ 'פתח-תקווה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בירושלים',
        routeParams: { term: [ 'ירושלים-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בחיפה',
        routeParams: { term: [ 'חיפה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה ברעננה',
        routeParams: { term: [ 'רעננה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה ברחובות',
        routeParams: { term: [ 'רחובות-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בחולון',
        routeParams: { term: [ 'חולון-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
    ].map(el => ({ ...el,
      type: LinkType.RouterLink,
      routeName: Route.SearchCommercial,
      routeParams: {
        ...el.routeParams,
        dealType: DealType.Rent,
        source: 'footer_link',
        filters: {
          ...el.routeParams.filters,
          priceRange: ZERO_TO_500_K,
          ppmRange: ZERO_TO_10_K_PPM,
        },
      },
    })),
  },
  ...baseFooterSections,
];

function seoFooterSectionsCreator(sections: FooterSection[], insertAfterHeader: string, sectionsToInsert: FooterSection[]): FooterSection[] {
  let result: FooterSection[] = [];
  for (const section of sections) {
    if (section.header === insertAfterHeader) {
      result = result.concat([ section, ...sectionsToInsert ]);
    }
    else {
      result.push(section);
    }
  }
  return result;
}

export const seoFooterSections: FooterSection[] = seoFooterSectionsCreator(footerSectionsResidential, RENT_RESIDENTIAL_SECTION_HEADER, [
  {
    header: 'נדל״ן מסחרי לקנייה',
    links: [
      {
        text: 'משרדים למכירה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'חנויות למכירה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ], filters: { propertyTypes: [ PropertyType.Shop ] } },
      },
      {
        text: 'משרדים למכירה ברמת גן',
        routeParams: { term: [ 'רמת-גן-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים למכירה בהרצליה',
        routeParams: { term: [ 'הרצליה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים למכירה בנתניה',
        routeParams: { term: [ 'נתניה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים למכירה בחולון',
        routeParams: { term: [ 'חולון-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים למכירה בחיפה',
        routeParams: { term: [ 'חיפה-ישראל' ], filters: {  propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים למכירה ברעננה',
        routeParams: { term: [ 'רעננה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים למכירה ברחובות',
        routeParams: { term: [ 'רחובות-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים למכירה בפתח תקווה',
        routeParams: { term: [ 'פתח-תקווה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
    ].map(el => ({ ...el,
      type: LinkType.RouterLink,
      routeName: Route.SearchCommercial,
      routeParams: {
        ...el.routeParams,
        dealType: DealType.Buy,
        source: 'footer_link',
        filters: {
          ...el.routeParams.filters,
          priceRange: ZERO_TO_10_M,
          ppmRange: ZERO_TO_100_K_PPM,
        },
      },
    })),
  },
  {
    header: 'נדל״ן מסחרי להשכרה',
    links: [
      {
        text: 'משרדים להשכרה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'חנויות להשכרה בתל אביב',
        routeParams: { term: [ 'תל-אביב-יפו-ישראל' ], filters: { propertyTypes: [ PropertyType.Shop ] } },
      },
      {
        text: 'משרדים להשכרה ברמת גן',
        routeParams: { term: [ 'רמת-גן-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בהרצליה',
        routeParams: { term: [ 'הרצליה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בנתניה',
        routeParams: { term: [ 'נתניה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בחולון',
        routeParams: { term: [ 'חולון-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בחיפה',
        routeParams: { term: [ 'חיפה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה ברעננה',
        routeParams: { term: [ 'רעננה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה ברחובות',
        routeParams: { term: [ 'רחובות-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
      {
        text: 'משרדים להשכרה בפתח תקווה',
        routeParams: { term: [ 'פתח-תקווה-ישראל' ], filters: { propertyTypes: [ PropertyType.Office ] } },
      },
    ].map(el => ({ ...el,
      type: LinkType.RouterLink,
      routeName: Route.SearchCommercial,
      routeParams: {
        ...el.routeParams,
        dealType: DealType.Rent,
        source: 'footer_link',
        filters: {
          ...el.routeParams.filters,
          priceRange: ZERO_TO_500_K,
          ppmRange: ZERO_TO_100_K_PPM,
        },
      },
    })),
  },
]);

export const getFooterSections = (marketplace: MarketplaceType) => marketplace === MarketplaceType.Residential ? footerSectionsResidential : footerSectionsCommercial;
